// frontend/src/components/TaskListExecutionModal.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Castle Traffic Ltd
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-10-16
// Updated:      2026-10-22 11:28 BST (responsive task cards)
// Description: Shared Task List execution modal for My Tasks, Advanced Task List and future My Day.
// Change Log:  2026-10-16 Initial grouped execution; sequential canonical completion and per-task failures.
//              2026-10-16 11:13 BST: Reuse ModalWrapper, theme action/general buttons, Quote Inbox-style status pills and task rows.
//              2026-10-21 16:00 BST: Approval gate actions, authoritative completion feedback.
//              2026-10-21 BST: Refresh runtime state after each completion; process ready tasks only;
//                              stop on guard failures; preserve visible execution results.
//              2026-10-22 11:28 BST: Responsive task cards; prevent mobile title compression;
//                              separate approval controls from task selection; retain desktop layout.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ModalWrapper from "./ModalWrapper";
import { useTheme } from "../context/ThemeContext.jsx";
import axiosInstance from "../api/axiosInstance";

const isCompleted = task => task.status === "completed";
const isReady = task => task.status === "ready";
const errorMessage = error => {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" ? detail : JSON.stringify(detail || error?.message || "Request failed");
};

export default function TaskListExecutionModal({ context, onClose }) {
  const { theme } = useTheme();
  const [detail, setDetail] = useState(null);
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState(null);
  const [decisionNote, setDecisionNote] = useState("");
  const busyRef = useRef(false);

  const load = useCallback(async () => {
    if (!context?.taskListId) return null;
    const { data } = await axiosInstance.get(`/task-lists/${context.taskListId}/execution`, { baseURL: "/api" });
    setDetail(data);
    setError("");
    return data;
  }, [context?.taskListId]);

  useEffect(() => {
    let active = true;
    if (!context?.taskListId) return undefined;
    axiosInstance.get(`/task-lists/${context.taskListId}/execution`, { baseURL: "/api" })
      .then(({ data }) => { if (active) { setDetail(data); setError(""); setSelected([]); setResults([]); } })
      .catch(e => { if (active) setError(errorMessage(e)); });
    return () => { active = false; };
  }, [context?.taskListId]);

  useEffect(() => {
    if (!context) return undefined;
    const prior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prior; };
  }, [context]);

  if (!context) return null;
  const tasks = detail?.tasks || [];
  const outstanding = tasks.filter(t => t.status !== "completed" && t.status !== "cancelled");
  const readyTasks = outstanding.filter(isReady);

  const execute = async (all) => {
    if (busyRef.current || !detail) return;
    const selectedIds = new Set(selected);
    const initialCandidates = outstanding.filter(t => all || selectedIds.has(t.id));
    if (!initialCandidates.length) return;

    busyRef.current = true;
    setBusy(true);
    setResults([]);
    setError("");
    const feedback = [];
    const attempted = new Set();
    let current = detail;
    let stopped = false;

    try {
      // Re-fetch authoritative state before starting; previous UI data may be stale.
      current = await load();
      if (!current) throw new Error("Task List execution data unavailable");

      // A successful completion may unlock the next task. Re-fetch after every completion.
      // The attempted set prevents loops if the API returns stale status.
      while (true) {
        const currentTasks = current.tasks || [];
        const eligible = currentTasks.find(task =>
          isReady(task) && !attempted.has(task.id) && (all || selectedIds.has(task.id))
        );
        if (!eligible) break;
        attempted.add(eligible.id);
        try {
          await axiosInstance.post(
            `/jobs/${current.job_id}/work-packages/${current.work_package_id}/tasks/${eligible.id}/complete`,
            { completion_note: null },
            { baseURL: "/api" }
          );
          current = await load();
          if (!current) throw new Error("Unable to refresh Task List after completion");
          const actual = (current.tasks || []).find(t => t.id === eligible.id);
          const finalStatus = actual?.status || "unknown";
          feedback.push({ id: eligible.id, title: eligible.title,
            ok: finalStatus === "completed",
            message: finalStatus === "waiting" ? "Awaiting approval" : `Status: ${finalStatus}` });
          if (finalStatus !== "completed") { stopped = true; break; }
        } catch (e) {
          feedback.push({ id: eligible.id, title: eligible.title, ok: false, message: errorMessage(e) });
          stopped = true;
          break;
        }
      }

      if (!stopped) {
        const remaining = (current.tasks || []).filter(task =>
          task.status !== "completed" && task.status !== "cancelled" && (all || selectedIds.has(task.id))
        );
        if (remaining.length) {
          feedback.push({
            id: "remaining",
            title: "Remaining tasks",
            ok: false,
            message: `${remaining.length} task(s) not ready for manual completion. Resolve workflow prerequisites or approvals before continuing.`,
          });
        }
      }
    } catch (e) {
      feedback.push({ id: "request", title: "Execution", ok: false, message: errorMessage(e) });
      try { await load(); } catch (_) { /* Keep the original failure visible. */ }
    } finally {
      setResults(feedback);
      setSelected([]);
      busyRef.current = false;
      setBusy(false);
      if (feedback.some(result => result.ok)) {
        window.dispatchEvent(new CustomEvent("vision:tasks-changed"));
      }
    }
  };

  const decideApproval = async () => {
    if (!decision || busyRef.current || !detail) return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    try {
      await axiosInstance.post(
        `/jobs/${detail.job_id}/work-packages/${detail.work_package_id}/tasks/${decision.id}/${decision.action}`,
        { decision_note: decisionNote.trim() || null },
        { baseURL: "/api" }
      );
      await load();
      setResults([{ id: decision.id, title: decision.title, ok: true,
        message: decision.action === "approve" ? "Approved" : "Rejected" }]);
      setDecision(null);
      setDecisionNote("");
      window.dispatchEvent(new CustomEvent("vision:tasks-changed"));
    } catch (e) { setError(errorMessage(e)); }
    finally { busyRef.current = false; setBusy(false); }
  };

  const pill = (status) => {
    const state = String(status || "pending").toLowerCase();
    const completed = state === "completed";
    const blocked = ["blocked", "failed", "waiting", "rejected"].includes(state);
    const dot = completed ? "#16a34a" : blocked ? "#dc2626" : theme?.accent_color || "#f97316";
    return {
      label: state.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
      dot,
      background: completed ? "rgba(22,163,74,.12)" : blocked ? "rgba(220,38,38,.12)" : "rgba(255,122,0,.12)",
      color: completed ? "#166534" : blocked ? "#991b1b" : "#7c2d12",
    };
  };

  const content = (
    <ModalWrapper onClose={busy ? undefined : onClose}>
      <div className="flex min-w-0 flex-col gap-3 text-gray-900" role="dialog" aria-modal="true" aria-label={detail?.name || "Task List"}>
        <div className="min-w-0 pr-8">
          <h2 className="text-lg font-bold break-words">{detail?.name || "Task List"}</h2>
          <p className="text-xs text-gray-500">Job #{detail?.job_id || context.jobId}</p>
        </div>
        {error && <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{String(error)}</p>}
        <div className="min-w-0 max-h-[52dvh] space-y-2 overflow-x-hidden overflow-y-auto pr-1">
          {tasks.map(task => {
            const done = isCompleted(task);
            const selectedNow = selected.includes(task.id);
            const status = pill(task.status);
            const selectable = isReady(task);
            return (
              <div
                key={task.id}
                className={`min-w-0 rounded-xl border border-gray-200 bg-white p-3 ${selectable ? "hover:bg-gray-50" : ""}`}
              >
                <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
                    <input
                      type="checkbox"
                      aria-label={`Select ${task.title}`}
                      className="mt-0.5 h-5 w-5 shrink-0 accent-current sm:mt-0"
                      checked={done || selectedNow}
                      disabled={busy || done || !selectable}
                      onChange={e => setSelected(prev => e.target.checked ? [...prev, task.id] : prev.filter(id => id !== task.id))}
                    />
                    <span className={`min-w-0 flex-1 whitespace-normal break-words text-sm font-semibold leading-5 ${done || selectedNow ? "line-through text-gray-400" : "text-gray-900"}`}>
                      {task.title}
                    </span>
                  </div>
                  <span
                    className="inline-flex w-fit shrink-0 items-center gap-2 self-start whitespace-nowrap rounded-full border border-black/10 px-2.5 py-1 text-[11px] font-black sm:self-center"
                    style={{ background: status.background, color: status.color }}
                  >
                    <span className="h-2 w-2 rounded-full" style={{ background: status.dot }} />
                    {status.label}
                  </span>
                </div>
                {task.approval_required && (
                  <div className="mt-3 min-w-0 border-t border-gray-100 pt-3 text-xs font-semibold text-amber-800" aria-label="Approval">
                    <div>{task.approval_pending ? "Approval required" : task.status === "completed" ? "Approved" : "Approval gate"}</div>
                    {task.approval_pending && task.can_decide_approval && (
                      <div className="mt-2 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                        <button
                          type="button"
                          disabled={busy}
                          className="min-h-[44px] rounded bg-green-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                          onClick={() => setDecision({ id: task.id, title: task.title, action: "approve" })}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          className="min-h-[44px] rounded bg-red-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                          onClick={() => setDecision({ id: task.id, title: task.title, action: "reject" })}
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {results.length > 0 && <div className="space-y-1" aria-live="polite">{results.map(r => <p key={r.id} className={`rounded p-2 text-xs ${r.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}>{r.title}: {r.message}</p>)}</div>}
        </div>
        {decision && <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" role="group" aria-label="Approval decision confirmation">
          <p className="text-sm font-semibold">{decision.action === "approve" ? "Approve" : "Reject"}: {decision.title}?</p>
          <textarea className="w-full rounded border border-gray-300 p-2 text-sm" rows={2} placeholder="Decision note (optional)" value={decisionNote} onChange={e => setDecisionNote(e.target.value)} disabled={busy}/>
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={busy} onClick={decideApproval} className="rounded bg-btnActionNormal px-3 py-2 text-white">Confirm {decision.action}</button>
            <button type="button" disabled={busy} onClick={() => { setDecision(null); setDecisionNote(""); }} className="rounded bg-btnGeneralNormal px-3 py-2 text-white">Cancel</button>
          </div>
        </div>}
        <div className="border-t border-gray-200 pt-3 space-y-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <button type="button" disabled={busy || !readyTasks.length} onClick={() => execute(true)} className="bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white px-3 py-2 rounded min-h-[44px] font-semibold disabled:opacity-40">{busy ? "Processing…" : "Complete All"}</button>
            <button type="button" disabled={busy || !selected.some(id => readyTasks.some(task => task.id === id))} onClick={() => execute(false)} className="bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white px-3 py-2 rounded min-h-[44px] font-semibold disabled:opacity-40">Complete Selected ({selected.length})</button>
          </div>
          <p className="text-xs text-gray-500">Complete All advances ready tasks in sequence and stops when a workflow guard requires intervention.</p>
          <div className="flex justify-between gap-3 text-sm font-semibold"><span>Overall Job Progress</span><span>{detail?.job_progress?.percent ?? 0}%</span></div>
          <div className="h-2 overflow-hidden rounded-full bg-gray-200"><div className="h-full bg-accent" style={{width: `${Math.min(100,Math.max(0,Number(detail?.job_progress?.percent ?? 0)))}%`, background: theme?.accent_color || undefined}}/></div>
          <p className="text-xs text-gray-500">{detail?.job_progress?.completed ?? 0} of {detail?.job_progress?.total ?? 0} executable job tasks completed</p>
        </div>
      </div>
    </ModalWrapper>
  );
  return createPortal(content, document.getElementById("modal-root") || document.body);
}
